import type { ThemeVariant } from '../../contract.js';
import { defaultLight, defaultDark } from '../default/variants.js';

/**
 * Obsidian family — promoted from `skin_obsidian` overlay during
 * the Driver Themes ticket and recolored purple (the original skin
 * was a dark slate; the new shader's dripping-sphere look pairs
 * with a violet palette).
 *
 * Light variant: inherits Default light chrome with purple accent
 * only. The shader's standalone dark palette dominates the
 * charging animation visual; the light UI chrome stays familiar
 * so drivers reading a menu page don't get whiplash from the
 * purple invading every surface.
 *
 * Dark variant: mid-bespoke. Spreads Default dark for the
 * chrome/widget recipes (the dense-glass treatments stay
 * consistent across families), then overrides surfaces + text
 * to a deep purple-black palette + accent. Card-glow box-shadow
 * follows the same 3-stop structure as Default dark / Luce dark
 * with the accent hue swapped to violet. Spec-named cardGlow
 * color `rgba(160,48,192,0.20)` is the base hue; expanded here
 * into the full 3-stop format the field expects.
 */
export const obsidianLight: ThemeVariant = {
  mode: 'light',
  tokens: {
    ...defaultLight.tokens,
    colors: {
      ...defaultLight.tokens.colors,
      accent: '#A030C0',
      accentSoft: 'rgba(160, 48, 192, 0.14)',
      charging: '#21c163',
      chargingGlow: 'rgba(33, 193, 99, 0.45)',
    },
  },
};

export const obsidianDark: ThemeVariant = {
  mode: 'dark',
  tokens: {
    ...defaultDark.tokens,
    colors: {
      ...defaultDark.tokens.colors,
      // Deep purple-black palette per the spec table. Surfaces lift
      // off the bg by ~10 luminance points so cards stay visible.
      bg: '#0A0510',
      surface: '#15081F',
      surface2: '#1F0C2D',
      border: '#2A1640',
      borderStrong: '#3D2356',
      accent: '#A030C0',
      accentSoft: 'rgba(160, 48, 192, 0.20)',
      charging: '#21c163',
      chargingGlow: 'rgba(33, 193, 99, 0.55)',
      text: '#F0E5FA',
      textMute: '#B5A0CC',
      mute: '#6B5A80',
      // Purple-tinted card glow stack. Same 3-stop structure as
      // Default dark / Luce dark for visual consistency; only the
      // hue swaps. Spec's `rgba(160,48,192,0.20)` is the base color;
      // expanded into the rim + bloom + drop shadow shape the field
      // expects.
      cardGlow:
        '0 0 0 1px color-mix(in srgb, #A030C0 20%, transparent),' +
        ' 0 0 24px -4px color-mix(in srgb, #A030C0 32%, transparent),' +
        ' 0 8px 32px -12px color-mix(in srgb, #A030C0 28%, transparent)',
      cardGlowSubtle:
        '0 0 0 1px color-mix(in srgb, #A030C0 10%, transparent),' +
        ' 0 0 14px -6px color-mix(in srgb, #A030C0 16%, transparent),' +
        ' 0 4px 12px -8px color-mix(in srgb, #A030C0 12%, transparent)',
      // Widget chrome — purple-tinted ring on the drop shadow plus
      // a slightly violet-tinted bg to tie the cards to the purple
      // page palette. Bg + blur values match Default dark's recipe
      // so the dense-glass behavior holds across families.
      widgetBg: 'rgba(21, 8, 31, 0.78)',
      widgetBorder: 'rgba(160, 48, 192, 0.14)',
      widgetShadow:
        '0 12px 32px rgba(0, 0, 0, 0.45),' +
        ' 0 0 0 1px rgba(160, 48, 192, 0.10)',
      chromeBg: 'rgba(21, 8, 31, 0.92)',
      chromeBorder: 'rgba(160, 48, 192, 0.12)',
      chromeShadow: '0 8px 24px rgba(0, 0, 0, 0.55)',
    },
  },
};
